import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { AdminLibrary, FilterLibrary, LookalikePair } from '@shared/services'
import { markLookalikesDifferent, mergeLookalike, readLookalikes } from '@shared/services'
import { useRemembered } from '../useRemembered'
import { ReportedSection, TrashSection } from './reviewSections'
import './autoFilter.css'
import './reviewQueue.css'

/**
 * The Review Queue (Huanrui, 2026-10-15)
 * -------------------------------------------------------------
 * Administrators only. Everything in the libraries that waits on a person:
 *
 * Three columns side by side (Huanrui, 2026-10-15): possible duplicates,
 * reported, trash; the switch at the top decides which libraries they show.
 *
 *  1. REPORTED: pictures somebody flagged with the red ! (moved here from the
 *     Auto Filter page, unchanged).
 *  2. POSSIBLE DUPLICATES: a picture that came in looking like one already
 *     there, though its bytes differ (docs/visual-duplicates.sql). Nothing is
 *     merged by itself. Keep one, and the other's tags and libraries go onto
 *     it and the other goes to the trash; or say they are different, and the
 *     pair is never asked about again.
 *  3. TRASH, per library (moved here from the Auto Filter page).
 *
 * A switch at the top shows one library, or all of them.
 */

type Shown = AdminLibrary | 'all'

const NAMES: Record<AdminLibrary, string> = {
  audit: 'Audit',
  'visual-language': 'Visual Language',
  'design-works': 'Design References',
  egnyte: 'Egnyte Inbox',
}
const SHOWN: Shown[] = ['all', 'audit', 'visual-language', 'design-works', 'egnyte']
const REPORTED: FilterLibrary[] = ['audit', 'visual-language', 'design-works']
const nameOf = (library: string) => NAMES[library as AdminLibrary] ?? library
const day = (iso: string) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '')

export function ReviewQueuePage() {
  const [shown, setShown] = useRemembered<Shown>('ww.review.library', 'all')
  const library: Shown = SHOWN.includes(shown) ? shown : 'all'
  const reportedIn = library === 'all' ? REPORTED : REPORTED.filter((one) => one === library)
  const trashIn: AdminLibrary[] = library === 'all' ? ['audit', 'visual-language', 'design-works', 'egnyte'] : [library]

  // Each column's count, added up from its libraries.
  const [counts, setCounts] = useState<Record<string, number>>({})
  const counter = useCallback(
    (key: string) => (n: number) => setCounts((now) => (now[key] === n ? now : { ...now, [key]: n })),
    [],
  )
  const reportedCounters = useMemo(() => Object.fromEntries(REPORTED.map((one) => [one, counter(`reported:${one}`)])), [counter])
  const trashCounters = useMemo(
    () => Object.fromEntries((['audit', 'visual-language', 'design-works', 'egnyte'] as const).map((one) => [one, counter(`trash:${one}`)])),
    [counter],
  )
  const sum = (kind: string, libraries: string[]) => {
    const known = libraries.map((one) => counts[`${kind}:${one}`])
    return known.some((n) => n === undefined) ? null : known.reduce<number>((a, b) => a + (b ?? 0), 0)
  }
  const reportedTotal = sum('reported', reportedIn)
  const trashTotal = sum('trash', trashIn)
  const duplicatesTotal = counts[`duplicates:${library}`]
  const many = library === 'all'

  return (
    <div className="afl rvq">
      <header className="afl__head">
        <div>
          <h1 className="afl__title">Review Queue</h1>
          <p className="afl__lede">
            Pictures waiting on a decision. Nothing here changes by itself.
          </p>
        </div>
        <span className="afl__spacer" />
        <div className="afl__seg afl__switch" role="group" aria-label="Which library">
          {SHOWN.map((one) => (
            <button key={one} type="button" aria-pressed={library === one} onClick={() => setShown(one)}>
              {one === 'all' ? 'All' : NAMES[one]}
            </button>
          ))}
        </div>
      </header>

      {/* Three columns (Huanrui, 2026-10-15): possible duplicates, reported, trash. */}
      <div className="rvq__cols">
        <section className="rvq__col">
          <header className="rvq__col-head">
            <h2>Possible duplicates</h2>
            <span className="rvq__count">{duplicatesTotal ?? '…'}</span>
          </header>
          <p className="rvq__col-note">Came in looking like a picture already there. Keep one, or say they differ.</p>
          <div className="rvq__col-body">
            <DuplicatesList key={`duplicates-${library}`} library={library} onCount={counter(`duplicates:${library}`)} />
          </div>
        </section>

        <section className="rvq__col">
          <header className="rvq__col-head">
            <h2>Reported</h2>
            <span className="rvq__count">{reportedIn.length === 0 ? 0 : reportedTotal ?? '…'}</span>
          </header>
          <p className="rvq__col-note">Flagged with the red !. Correct the tags, then keep or trash.</p>
          <div className="rvq__col-body">
            {reportedIn.map((one) => (
              <ReportedSection key={`reported-${one}`} library={one} label={NAMES[one]} plain onCount={reportedCounters[one]} />
            ))}
            {reportedIn.length === 0 ? <p className="afl__quiet">Egnyte Inbox pictures are not reported.</p> : null}
            {reportedIn.length > 0 && reportedTotal === 0 ? <p className="afl__quiet">Nothing reported{many ? '' : ` in ${NAMES[library as AdminLibrary]}`}.</p> : null}
          </div>
        </section>

        <section className="rvq__col">
          <header className="rvq__col-head">
            <h2>Trash</h2>
            <span className="rvq__count">{trashTotal ?? '…'}</span>
          </header>
          <p className="rvq__col-note">Deleted for good 30 days after they were put there, unless restored.</p>
          <div className="rvq__col-body">
            {trashIn.map((one) => (
              <TrashSection key={`trash-${one}`} library={one} label={NAMES[one]} plain onCount={trashCounters[one]} />
            ))}
            {trashTotal === 0 ? <p className="afl__quiet">The trash is empty.</p> : null}
          </div>
        </section>
      </div>
    </div>
  )
}

/* -------------------------------------------------------------------------
   Possible duplicates
   ------------------------------------------------------------------------- */

function DuplicatesList({ library, onCount }: { library: Shown; onCount: (count: number) => void }) {
  const [pairs, setPairs] = useState<LookalikePair[] | null>(null)
  const [total, setTotal] = useState(0)
  const [error, setError] = useState('')
  const [working, setWorking] = useState('')

  const load = useCallback(() => {
    readLookalikes(library)
      .then((found) => {
        setPairs(found.pairs)
        setTotal(found.total)
        onCount(found.total)
        setError('')
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Could not read the possible duplicates'))
  }, [library])
  useEffect(load, [load])

  const gone = (pairId: string) => {
    setPairs((now) => (now ? now.filter((one) => one.id !== pairId) : now))
    setTotal((now) => {
      const next = Math.max(0, now - 1)
      onCount(next)
      return next
    })
  }
  /*
   * SAME / NOT THE SAME (Huanrui, 2026-10-15). Two buttons a pair, and one at
   * the top for all of them. Nobody picks which picture stays: the larger one
   * does, or, the same size, the one already there. Either way the other's
   * tags and libraries go onto it (a merge, not a choice), and the other goes
   * to the trash, restorable for 30 days.
   */
  const keeperOf = (pair: LookalikePair): string => {
    const a = pair.incoming.width * pair.incoming.height
    const b = pair.existing.width * pair.existing.height
    return a > b * 1.01 ? pair.incoming.assetId : pair.existing.assetId
  }
  const [askAll, setAskAll] = useState(false)
  const [allDone, setAllDone] = useState<{ done: number; of: number } | null>(null)
  const allTimer = useRef<number | undefined>(undefined)
  useEffect(() => () => window.clearTimeout(allTimer.current), [])

  /** Every pair shown, one after another: a merge re-points the pairs after it. */
  const allSame = async () => {
    if (!pairs || pairs.length === 0) return
    const list = [...pairs]
    setError('')
    setAllDone({ done: 0, of: list.length })
    let failed = 0
    for (let at = 0; at < list.length; at += 1) {
      const pair = list[at]!
      setWorking(pair.id)
      try {
        await mergeLookalike(pair.id, keeperOf(pair))
      } catch (err) {
        // Dealt with by an earlier merge in this run (it shared a picture): fine.
        if (!(err instanceof Error && /dealt with already/i.test(err.message))) failed += 1
      }
      setAllDone({ done: at + 1, of: list.length })
    }
    setWorking('')
    setAllDone(null)
    window.dispatchEvent(new Event('ww:trash-changed'))
    if (failed > 0) setError(`${failed} could not be merged. They are still in the list.`)
    // Read again: merges can leave new pairs behind (a third picture like both).
    load()
  }

  const act = (pairId: string, work: () => Promise<unknown>, after?: () => void) => {
    setWorking(pairId)
    setError('')
    work()
      .then(() => {
        gone(pairId)
        after?.()
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'That did not work'))
      .finally(() => setWorking(''))
  }

  return (
    <div className="rvq__dups">
      {error ? <p className="afl__error">{error}</p> : null}
      {pairs && pairs.length > 0 ? (
        <div className="rvq__all">
          <button
            type="button"
            className={`afl__btn afl__btn--go rvq__all-btn${askAll ? ' rvq__all-btn--ask' : ''}`}
            disabled={allDone !== null || working !== ''}
            onClick={() => {
              if (!askAll) {
                setAskAll(true)
                window.clearTimeout(allTimer.current)
                allTimer.current = window.setTimeout(() => setAskAll(false), 4000)
                return
              }
              window.clearTimeout(allTimer.current)
              setAskAll(false)
              void allSame()
            }}
          >
            {allDone
              ? `Merging ${allDone.done} of ${allDone.of}…`
              : askAll
                ? `Merge all ${pairs.length}? Click again`
                : `All the same (${pairs.length})`}
          </button>
          <span className="rvq__all-note">
            Each pair becomes one picture with both sets of tags and libraries; the other goes to the trash (30 days).
          </span>
        </div>
      ) : null}
      {pairs && pairs.length === 0 ? <p className="afl__quiet">Nothing waiting.</p> : null}
      {pairs && pairs.length > 0 ? (
        <div className="rvq__pairs">
          {pairs.map((pair) => {
            const busy = working === pair.id
            const keeper = keeperOf(pair)
            const same = () =>
              act(pair.id, () => mergeLookalike(pair.id, keeper), () => window.dispatchEvent(new Event('ww:trash-changed')))
            return (
              <article key={pair.id} className={`rvq__pair${busy ? ' rvq__pair--busy' : ''}`}>
                <header className="rvq__pair-head">
                  <span className={`rvq__band rvq__band--${pair.band}`}>
                    {pair.band === 'same' ? 'Almost certainly the same' : 'Possibly the same'}
                  </span>
                  <span className="rvq__measure" title="How many of 64 shape bits differ, and how far apart the colours are">
                    shape {pair.distance}/64 · colour {pair.colourGap}
                  </span>
                  <span className="afl__spacer" />
                  <button
                    type="button"
                    className="afl__btn afl__btn--go rvq__decide"
                    disabled={busy || allDone !== null}
                    onClick={same}
                    title="One picture: the one marked Kept, with both pictures' tags and libraries. The other goes to the trash."
                  >
                    {busy ? 'Merging…' : 'Same'}
                  </button>
                  <button
                    type="button"
                    className="afl__btn rvq__decide"
                    disabled={busy || allDone !== null}
                    onClick={() => act(pair.id, () => markLookalikesDifferent(pair.id))}
                    title="Two different pictures: both stay, and this pair is never asked about again"
                  >
                    Not the same
                  </button>
                </header>
                <div className="rvq__two">
                  {[
                    { one: pair.incoming, role: 'Came in' },
                    { one: pair.existing, role: 'Already there' },
                  ].map(({ one, role }) => (
                    <div key={one.assetId} className="rvq__side">
                      <div className="rvq__thumb">
                        {one.thumb ? <img src={one.thumb} alt={one.title} loading="lazy" /> : null}
                        {keeper === one.assetId ? <span className="rvq__sharper">Kept</span> : null}
                      </div>
                      <div className="rvq__meta">
                        <span className="rvq__role">{role}</span>
                        <strong className="rvq__title" title={one.title}>
                          {one.title || 'Untitled'}
                        </strong>
                        <span className="rvq__line">
                          {one.width && one.height ? `${one.width} × ${one.height}` : 'size unknown'}
                          {one.source ? ` · from ${one.source}` : ''}
                          {one.addedAt ? ` · ${day(one.addedAt)}` : ''}
                        </span>
                        <span className="rvq__libs">
                          {one.libraries.map((lib) => (
                            <span key={lib} className="rvq__lib">
                              {nameOf(lib)}
                            </span>
                          ))}
                        </span>
                        {one.tags.length > 0 ? (
                          <span className="rvq__tags" title={one.tags.map((tag) => tag.name).join(', ')}>
                            {one.tags
                              .slice(0, 10)
                              .map((tag) => tag.name)
                              .join(' · ')}
                            {one.tags.length > 10 ? ` · +${one.tags.length - 10}` : ''}
                          </span>
                        ) : (
                          <span className="rvq__tags rvq__tags--none">No tags</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </article>
            )
          })}
          {total > pairs.length ? (
            <p className="afl__quiet">
              {pairs.length} of {total} shown; the rest appear as these are dealt with.
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
